'use client'

import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog'
import { useSearchState } from '@/lib/search-state'
import { clsx } from 'clsx'
import { ChevronRight, Command, CornerDownRight, FileText, Search, X } from 'lucide-react'
import Link from 'next/link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { twMerge } from 'tailwind-merge'

function cn(...inputs) {
  return twMerge(clsx(inputs))
}

export function PagefindSearch() {
  const { open, toggle, close } = useSearchState()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)

  const inputRef = useRef(null)
  const pagefindRef = useRef(null)

  // Pagefind ships a wasm index (~90 KB); loading it on mount blocks the main thread
  // during page load for a search nobody opened yet. Load on first open instead.
  const loadPagefind = useCallback(() => {
    if (!pagefindRef.current) {
      pagefindRef.current = import(/* webpackIgnore: true */ '/_pagefind/pagefind.js')
        .then(async (pf) => {
          await pf.init()
          return pf
        })
        .catch((e) => {
          console.warn('Pagefind index not found. Run `npm run build` to generate it.', e)
          pagefindRef.current = null
          return null
        })
    }
    return pagefindRef.current
  }, [])

  useEffect(() => {
    if (open) loadPagefind()
  }, [open, loadPagefind])

  // Reset query when closed
  useEffect(() => {
    if (!open) {
      setQuery('')
      setResults([])
    }
  }, [open])

  // Keyboard shortcuts (Cmd+K)
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        if (e.defaultPrevented) return
        e.preventDefault()
        toggle()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [toggle])

  // Search logic
  useEffect(() => {
    if (!query.trim()) {
      setResults([])
      setLoading(false)
      setSelectedIndex(0)
      return
    }

    setLoading(true)
    const timer = setTimeout(async () => {
      try {
        const pf = await loadPagefind()
        if (!pf) {
          setResults([])
          return
        }
        const primaryRes = await pf.search(query)
        let finalResultRefs = [...primaryRes.results]

        // If primary search results are sparse, split by words and search for each (OR-like behavior)
        if (finalResultRefs.length < 5 && query.trim().includes(' ')) {
          const words = query
            .trim()
            .split(/\s+/)
            .filter((w) => w.length >= 3)

          if (words.length > 0) {
            const wordSearches = await Promise.all(words.map((w) => pf.search(w)))
            const seenIds = new Set(finalResultRefs.map((r) => r.id))

            wordSearches.forEach((ws) => {
              ws.results.forEach((r) => {
                if (!seenIds.has(r.id)) {
                  seenIds.add(r.id)
                  finalResultRefs.push(r)
                }
              })
            })
          }
        }

        // Fetch data for the first 10 unique results
        const data = await Promise.all(finalResultRefs.slice(0, 10).map((r) => r.data()))
        setResults(data)
        setSelectedIndex(0)
      } catch (e) {
        console.error('Search failed', e)
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 200)

    return () => clearTimeout(timer)
  }, [query, loadPagefind])

  // Keyboard navigation within results
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev + 1) % (results.length || 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex((prev) => (prev - 1 + (results.length || 1)) % (results.length || 1))
      } else if (e.key === 'Enter') {
        if (results[selectedIndex]) {
          const url = normaliseUrl(results[selectedIndex].url)
          window.location.href = url
          close()
        }
      } else if (e.key === 'Escape') {
        close()
      }
    },
    [results, selectedIndex, close],
  )

  const normaliseUrl = (url) => url.replace(/\.html$/, '').replace(/\/index$/, '/') || '/'

  const getBreadcrumbs = (url) => {
    const parts = url
      .split('/')
      .filter(Boolean)
      .map((p) => p.replace(/-/g, ' ').toUpperCase())
    if (parts.length > 1) {
      return parts.slice(0, -1)
    }
    return []
  }

  return (
    <>
      {/* Search Trigger */}
      <button
        onClick={toggle}
        className="group relative flex items-center gap-2 px-3 py-1.5 text-sm font-medium transition-all duration-200 
          bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 
          border border-neutral-200 dark:border-neutral-700 rounded-lg w-48 lg:w-64"
      >
        <Search className="w-4 h-4 text-neutral-500 dark:text-neutral-400 group-hover:text-neutral-900 dark:group-hover:text-white transition-colors" />
        <span className="text-neutral-500 dark:text-neutral-400 group-hover:text-neutral-600 dark:group-hover:text-neutral-300 transition-colors flex-1 text-left">
          Search...
        </span>
        <div className="flex items-center gap-1 opacity-60">
          <kbd className="hidden sm:inline-flex items-center justify-center min-w-[20px] h-5 px-1 font-sans text-[0.625rem] font-bold border border-neutral-300 dark:border-neutral-600 rounded bg-neutral-50 dark:bg-neutral-900">
            ⌘
          </kbd>
          <kbd className="hidden sm:inline-flex items-center justify-center min-w-[20px] h-5 px-1 font-sans text-[0.625rem] font-bold border border-neutral-300 dark:border-neutral-600 rounded bg-neutral-50 dark:bg-neutral-900">
            K
          </kbd>
        </div>
      </button>

      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setQuery('')
            close()
          }
        }}
      >
        <DialogContent
          showCloseButton={false}
          className="mx-auto max-w-[95vw] sm:max-w-2xl w-full p-0 bg-white dark:bg-neutral-900 rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-800 flex flex-col h-full min-h-[40vh] max-h-[85vh] sm:max-h-[50vh] gap-0 translate-y-[-50%] sm:translate-y-[-50%] top-[55%]! sm:top-1/2"
        >
          <DialogTitle className="sr-only">Search Documentation</DialogTitle>
          <DialogDescription className="sr-only sm:text-sm! text-xs!">
            Search through technical documentation, guides, and tools.
          </DialogDescription>
          <button
            onClick={() => {
              if (open) {
                setQuery('')
                close()
              }
            }}
            className="p-1.5 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-full transition-colors flex md:hidden bg-neutral-7000 dark:bg-black/70 -top-3 -right-2 absolute z-50"
          >
            <X className="size-5 text-neutral-500" />
          </button>

          {/* Header & Input */}
          <div className="relative flex items-center px-4 py-4 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50">
            <Search className="w-5 h-5 text-neutral-400 mr-3 shrink-0" />
            <input
              ref={inputRef}
              autoFocus
              type="text"
              className="flex-1 bg-transparent text-base md:text-lg outline-none text-neutral-900 dark:text-white placeholder-neutral-400"
              placeholder="Search documentation, guides, and tools..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="p-1 hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded-full transition-colors bg-neutral-100 dark:bg-neutral-800"
              >
                <X className="w-4 h-4 text-neutral-500" />
              </button>
            )}
            <div className="hidden md:flex ml-3 px-2 py-1 text-[10px] font-bold text-neutral-400 border border-neutral-200 dark:border-neutral-700 rounded bg-white dark:bg-neutral-800">
              ESC
            </div>
          </div>

          {/* Result Content Area */}
          <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
            {!query && (
              <div className="py-12 flex flex-col items-center justify-center text-center px-4">
                <div className="w-12 h-12 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-4 transition-transform hover:scale-110">
                  <Command className="w-6 h-6 text-neutral-400" />
                </div>
                <h3 className="text-neutral-900 dark:text-white font-semibold">No active search</h3>
                <p className="text-sm text-neutral-500 max-w-[240px] mt-1">
                  Type a few words to find titles, headings, and detailed content.
                </p>
              </div>
            )}

            {query && results.length === 0 && !loading && (
              <div className="py-12 flex flex-col items-center justify-center text-center px-4">
                <div className="w-12 h-12 rounded-2xl bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-4">
                  <Search className="w-6 h-6 text-neutral-400" />
                </div>
                <h3 className="text-neutral-900 dark:text-white font-semibold">No results found</h3>
                <p className="text-sm text-neutral-500 mt-1">
                  We couldn&apos;t find anything matching &ldquo;{query}&rdquo;
                </p>
              </div>
            )}

            {loading && (
              <div className="py-12 flex flex-col items-center justify-center text-center px-4">
                <div className="w-8 h-8 rounded-full border-2 border-neutral-300 dark:border-neutral-700 border-t-neutral-900 dark:border-t-white animate-spin mb-4" />
                <p className="text-sm text-neutral-500 animate-pulse font-medium">Searching Documentation...</p>
              </div>
            )}

            {query && results.length > 0 && (
              <div className="space-y-4 py-2">
                {/* Ask AI Suggestion Block (Premium touch) */}
                {/* <div className="mx-2 p-4 bg-neutral-50 dark:bg-neutral-800/10 rounded-xl border border-neutral-200 dark:border-neutral-700/50 flex flex-col gap-3 group/ai transition-all hover:bg-neutral-100 dark:hover:bg-neutral-800/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-(--supra-red)" />
                      <span className="text-xs font-bold text-neutral-900 dark:text-white">
                        Ask &ldquo;{query.length > 40 ? query.slice(0, 40) + '...' : query}&rdquo;
                      </span>
                    </div>
                  </div>
                  <div className="text-[12px] text-neutral-600 dark:text-neutral-400 leading-relaxed font-medium pl-6">
                    Supra delivers the one thing AI Agents truly need: a unified, real-time decentralized data and
                    execution layer. Click to ask our AI assistant for a deeper dive.
                  </div>
                </div> */}

                <div className="px-2 pb-2">
                  <h4 className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-2">
                    Search Results
                  </h4>
                  <ul className="space-y-1">
                    {results.map((result, index) => {
                      const breadcrumbs = getBreadcrumbs(result.url)
                      const isSelected = index === selectedIndex

                      return (
                        <li key={result.url}>
                          <Link
                            href={normaliseUrl(result.url)}
                            onClick={close}
                            onMouseEnter={() => setSelectedIndex(index)}
                            className={cn(
                              'group block p-3 rounded-xl border-l-2 transition-all duration-150',
                              isSelected
                                ? 'bg-neutral-100 dark:bg-neutral-800 border-(--supra-red)'
                                : 'border-transparent hover:bg-neutral-50 dark:hover:bg-neutral-800/50',
                            )}
                          >
                            <div className="flex flex-col gap-1.5">
                              {/* Breadcrumbs */}
                              {breadcrumbs.length > 0 && (
                                <div className="flex items-center gap-1 text-[10px] font-bold text-neutral-400">
                                  {breadcrumbs.map((bc, i) => (
                                    <span key={i} className="flex items-center gap-1">
                                      {bc}
                                      {i < breadcrumbs.length - 1 && <ChevronRight className="w-2.5 h-2.5" />}
                                    </span>
                                  ))}
                                </div>
                              )}

                              {/* Title */}
                              <div className="flex items-center gap-2">
                                <FileText
                                  className={cn(
                                    'w-4 h-4 transition-colors',
                                    isSelected ? 'text-(--supra-red)' : 'text-neutral-400 group-hover:text-neutral-600',
                                  )}
                                />
                                <span
                                  className={cn(
                                    'text-sm font-bold transition-colors',
                                    isSelected ? 'text-(--supra-red)' : 'text-neutral-900 dark:text-white',
                                  )}
                                >
                                  {result.meta?.title?.split('|')[0].trim() ?? 'Untitled'}
                                </span>
                              </div>

                              {/* Excerpt */}
                              <p
                                className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed font-medium line-clamp-2
                                  [&_mark]:bg-(--supra-red)/10 [&_mark]:dark:bg-(--supra-red)/20
                                  [&_mark]:text-(--supra-red) [&_mark]:font-bold [&_mark]:rounded-sm [&_mark]:px-0.5"
                                dangerouslySetInnerHTML={{ __html: result.excerpt }}
                              />

                              {/* Sub results (headings) */}
                              {isSelected && result.sub_results && result.sub_results.length > 0 && (
                                <div className="mt-2 pl-4 border-l border-neutral-200 dark:border-neutral-700 space-y-2">
                                  {result.sub_results.slice(0, 2).map((sub, i) => (
                                    <div key={i} className="flex flex-col gap-1">
                                      <div className="flex items-center gap-2 text-[11px] font-bold text-neutral-700 dark:text-neutral-300">
                                        <CornerDownRight className="w-3 h-3 text-neutral-400" />
                                        {sub.title}
                                      </div>
                                      <p
                                        className="text-[10px] text-neutral-500 leading-relaxed line-clamp-1 pl-5
                                          [&_mark]:bg-(--supra-red)/5 [&_mark]:text-(--supra-red) [&_mark]:font-bold"
                                        dangerouslySetInnerHTML={{ __html: sub.excerpt }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </Link>
                        </li>
                      )
                    })}
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* Footer / Shortcuts */}
          <div className="px-4 py-3 bg-neutral-50 dark:bg-neutral-950 border-t border-neutral-100 dark:border-neutral-800 flex items-center justify-between text-[11px] font-bold text-neutral-400 rounded-b-2xl">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-1.5">
                <span className="px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800">
                  ENTER
                </span>
                to select
              </div>
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  <span className="px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 leading-none">
                    ↑
                  </span>
                  <span className="px-1 py-0.5 rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 leading-none">
                    ↓
                  </span>
                </div>
                to navigate
              </div>
            </div>

            <div className="flex items-center gap-3">
              <span className="hover:text-neutral-600 dark:hover:text-neutral-200 cursor-pointer transition-colors">
                Filter: All docs
              </span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 5px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #333;
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #444;
        }
      `}</style>
    </>
  )
}
